import throttle from 'lodash.throttle';
import arraySymmetricDifference from '~/helpers/arraySymmetricDifference';
import capitalize from '~/helpers/capitalize';
import euclideanDistance from '~/helpers/euclideanDistance';
import pathToString from '~/helpers/pathToString';
import relativize from '~/helpers/relativize';
import { getLineLength, isCurved } from '~/helpers/revealCircle';
import EE from '~/listenerEventService/EventEmitter';
import { clickstreamLogger } from '~/logging';
import { findMatchingRulesForTree } from '~/utils/NodeMatcher';
import settings from '~/utils/Settings';
import { overlayDisabledButtons, removeButtonOverlays } from '~/utils/disabledButtonsOverlay';

const spaces = /\s+/g;

const MAX_VALUE_LENGTH = 1000;

const MAX_CURRENT_STEP = 3;

const circleMovementLogsEmptyObject = {
  value: null,
  processedTarget: null,
  target: null,
  coordinate: null,
  relativeCoordinate: null,
};

const createOrderKeywords = [
  'создать заказ',
  'оформить заказ',
  'order',
  'купить все товары в корзине',
  'оплатить',
  'быстрый заказ',
  'checkout',
  'перейти к оформлению',
];

const orderDetailsKeywords = ['product', 'collection', 'catalog', 'products'];

const successfulOrderKeywords = [
  'ваш заказ принят',
  'спасибо за покупку',
  'оплата совершена',
  'товар оплачен',
  'заказ оплачен',
];

const unsuccessfulOrderKeywords = ['заказ отменен', 'оплата отменена'];

const responseBodyKeywords = {
  ids: ['id', 'id_item'],
  carts: ['cart', 'products', 'items'],
  productNames: ['name', 'title_item', 'title'],
  productPrices: ['unitPrice', 'price', 'actual_price_item'],
};

const addedToCartUrlKeywords = ['add', 'buy'];

const removedFromCartUrlKeywords = ['rem', 'del'];

const findKeywordInNode = (keywords, currElement, checkAttributes) => {
  if (currElement) {
    for (const keyword of keywords) {
      if (currElement.attributes.length > 0 && checkAttributes) {
        for (const attribute of currElement.attributes) {
          if (attribute.nodeValue.toLowerCase().includes(keyword)) return true;
        }
      }

      if (currElement.textContent.toLowerCase().includes(keyword)) return true;
    }
  }
};

const containsCreateOrderButton = (t) => {
  let currElement;

  const elements = document.querySelectorAll('a, button, svg, input');

  for (const element of elements) {
    if (element.contains(t)) {
      currElement = element;
    }
  }

  return findKeywordInNode(createOrderKeywords, currElement, true);
};

const containsLinkToDetails = (t) => {
  if (t.tagName === 'A') {
    for (const orderDetailsKeyword of orderDetailsKeywords) {
      const matchKeyword = new RegExp(`${orderDetailsKeyword}\/(.+)`);

      if (t.getAttribute('href')?.match(matchKeyword)) return true;
    }
  }
};

const findPropInObject = (obj, name, prevObject, defaultObject) => {
  for (const [key, value] of Object.entries(obj)) {
    if (key === name) return value;

    if (value.constructor.name === 'Object') {
      if (!Object.keys(value).length) {
        Object.defineProperty(obj, key, {
          enumerable: false,
        });

        return findPropInObject(prevObject, name, defaultObject, defaultObject);
      }

      return findPropInObject(value, name, obj, defaultObject);
    }

    Object.defineProperty(obj, key, {
      enumerable: false,
    });

    if (!Object.keys(obj).length)
      return findPropInObject(prevObject, name, defaultObject, defaultObject);

    return findPropInObject(obj, name, prevObject, defaultObject);
  }
};

export default class MouseListener extends EE {

  get nodeMatchingRules() {
    return settings.getOrDefault('ELEMENT_MATCHING_RULES', {});
  }

  get minimalMousePositionDistance() {
    return settings.getOrDefault('MOUSE_MINIMAL_DISTANCE', 5);
  }

  get minimalMousePositionPeriod() {
    return settings.getOrDefault('MOUSE_MINIMAL_PERIOD', 600);
  }

  get checkExtraClickTimeout() {
    return settings.getOrDefault('CHECK_EXTRA_CLICK_TIMEOUT', 400);
  }

  get checkExtraClickRadius() {
    return settings.getOrDefault('CHECK_EXTRA_CLICK_RADIUS', 40);
  }
  static MOUSE_CLOSE_MOVE_THROTTLE_TIMEOUT = 70;
  static MOUSE_POSITIONS_COUNT = 5;
  static MOUSE_VERTICAL_LIMIT = 100;
  static MOUSE_GESTURE_TIMEOUT = 2000;
  static MOUSE_MOVE_THROTTLE_TIMEOUT = 1000 / 30;
  static POINTS_OF_CURVATURE_COUNT = 10;

  /**
   *
   * @param logs
   * @returns {*}
   */
  static formatMovementLogs(logs) {
    const middleElemNumber = 5;
    if (logs[middleElemNumber] !== undefined) {
      return logs[middleElemNumber];
    }

    return circleMovementLogsEmptyObject;
  }

  lastMousePosition = null;
  lastMousePositionEventTime = null;
  lastMousePositionOverElement = [];
  previousClick = null;
  clickTimer = null;
  lastMousePositions = [];

  // Свойства для обслуживание события circe-movement.
  mouseHistory = [];
  circleMovementLogs = [];

  mouseHistoryString = '';
  middleLineLengths = [];
  middleLineLength;
  handleCloseMove = throttle(
    (e) => {
      this.lastMousePositions.push(e.clientY);

      if (
        this.lastMousePositions.length !==
        MouseListener.MOUSE_POSITIONS_COUNT + 1
      )
        return;

      this.lastMousePositions.shift();

      for (let i = 0; i <= MouseListener.MOUSE_POSITIONS_COUNT; i += 1) {
        const prev = this.lastMousePositions[i - 1];

        if (!prev) continue;

        const current = this.lastMousePositions[i];

        if (current >= prev) break;

        if (
          !this.lastMousePositions[i + 1] &&
          current < MouseListener.MOUSE_VERTICAL_LIMIT
        ) {
          this.emit('analytics', {
            type: 'close-gesture',
            save: false,
          });
        }
      }
    },
    MouseListener.MOUSE_CLOSE_MOVE_THROTTLE_TIMEOUT,
    { trailing: false }
  );
  handleCloseMoveAfter = throttle(
    () => {
      this.lastMousePositions = [];
    },
    MouseListener.MOUSE_GESTURE_TIMEOUT,
    { leading: false }
  );
  onMove = throttle((e) => {
    if (e && e.target && e.pageX && e.pageY) {
      const [target, x, y] = [e.target, e.pageX, e.pageY];
      const result = {};
      const matching = findMatchingRulesForTree(
        target,
        this.nodeMatchingRules,
        result
      );
      const { x: relativeX, y: relativeY } = relativize(x, y, result.node);
      const eventTime = new Date().getTime();
      const distanceCondition =
        this.lastMousePosition !== null &&
        euclideanDistance({ x, y }, this.lastMousePosition) <
        this.minimalMousePositionDistance;
      const timeCondition =
        this.lastMousePositionEventTime !== null &&
        eventTime - this.lastMousePositionEventTime <
        this.minimalMousePositionPeriod;
      const elementCondition =
        this.lastMousePositionOverElement !== null &&
        this.lastMousePositionOverElement.length &&
        matching.length &&
        arraySymmetricDifference(matching, this.lastMousePositionOverElement)
          .length === 0;
      if (distanceCondition && timeCondition && elementCondition) {
        return;
      }

      this.lastMousePosition = {
        x,
        y,
      };
      this.lastMousePositionEventTime = eventTime;
      this.lastMousePositionOverElement = matching;
      clickstreamLogger.debug(
        `Mouse position (${x}, ${y}) relativePosition (${relativeX}, ${relativeY}) above element: `,
        target
      );
      this.emit('analytics', {
        type: 'mouse-position',
        processedTarget: matching.length
          ? matching.join(', ')
          : pathToString(target),
        target,
        x,
        y,
        relativeX,
        relativeY,
      });
    }
  }, MouseListener.MOUSE_MOVE_THROTTLE_TIMEOUT);

  handleCircleMovement = throttle((e) => {
    // Если история пуста, инициализируем начальное значение.
    if (this.mouseHistory.length === 0) {
      this.addInitialValueToMouseHistory(e);
    }

    // Наполняем массив значений дуги точками.
    if (this.mouseHistory.length > 0) {
      this.addValueToMouseHistory(e);
    }

    const pointsCount = MouseListener.POINTS_OF_CURVATURE_COUNT;
    // Если в истории зафиксировано достаточно точек.
    if (this.mouseHistory.length >= pointsCount) {
      // И кривизна дуги достаточна для того, чтобы считать движение круговым, шлем логи на сервер.
      if (isCurved(this.mouseHistory)) {
        this.sendCircleMovementData();
      }
      this.clearCircleMovementData();
    }
  }, MouseListener.MOUSE_MOVE_THROTTLE_TIMEOUT / 10);

  /**
   * Method to ignore extra two clicks before double click
   * and extra click on radio or checkbox when clicking on the label
   * @param {*} e
   * @returns
   */
  onClick = (e) => {
    if (e && e.target && e.type && e.pageX && e.pageY) {
      // Timer has been already set
      if (this.clickTimer && this.previousClick) {
        switch (e.type) {
          case 'click':
            const distance = euclideanDistance(
              { x: this.previousClick.pageX, y: this.previousClick.pageY },
              { x: e.pageX, y: e.pageY }
            );
            const withinRadius = distance <= this.checkExtraClickRadius;

            if (withinRadius) {
              return;
            } else {
              this.processClick(this.previousClick);
              this.clickTimer = setTimeout(
                this.processClick,
                this.checkExtraClickTimeout,
                e
              );
              this.previousClick = e;
            }
            break;
          case 'dblclick':
            // Ignore previous click and process current click
            this.processClick(e);
            break;
          default:
            this.processClick(this.previousClick);
            this.processClick(e);
        }
      } else {
        // Set timer
        this.clickTimer = setTimeout(
          this.processClick,
          this.checkExtraClickTimeout,
          e
        );
        // Save current click
        this.previousClick = e;
      }
    }
  }

  processClick = (e) => {
    const [target, which, x, y, button] = [
      e.target,
      e.type,
      e.pageX,
      e.pageY,
      e.button,
    ];

    clearTimeout(this.clickTimer);
    this.clickTimer = null;
    this.previousClick = null;

    const value = this.getValue(target);

    const result = {};
    const matching = findMatchingRulesForTree(
      target,
      this.nodeMatchingRules,
      result
    );
    const { x: relativeX, y: relativeY } = relativize(x, y, result.node);

    const isLeftClick = button === 0;
    const isMiddleClick = button === 1;

    const isCreateOrderButton =
      isLeftClick && containsCreateOrderButton(target);
    const isItemDetailsButton =
      (isLeftClick || isMiddleClick) && containsLinkToDetails(target);
    const isOrderSuccessful =
      isLeftClick && findKeywordInNode(successfulOrderKeywords, document.body);
    const isOrderUnsuccessful =
      isLeftClick &&
      findKeywordInNode(unsuccessfulOrderKeywords, document.body);

    const constantMock = window.fetch;

    const that = this;

    // интерсептор для дефолтного fetch
    // нельзя ничего делать с res, кроме клонирования, все операции - только с клоном
    window.fetch = async function () {
      const res = constantMock.apply(this, arguments);
      const clonedResponse = await res.then(r => r.clone());

      try {
        const payload = {
          value: null,
          cart: null,
        };
        const cartItemInfo = {
          id: null,
          name: null,
          price: null,
        };

        let cart = null;
        let isAddingToCart = null;
        let isRemovingItem = null;

        for (const addedToCardUrlKeyword of addedToCartUrlKeywords) {
          if (
            clonedResponse.url.includes(addedToCardUrlKeyword) &&
            !isAddingToCart
          ) {
            isAddingToCart = true;
          }
        }

        for (const removedFromCartUrlKeyword of removedFromCartUrlKeywords) {
          if (
            clonedResponse.url.includes(removedFromCartUrlKeyword) &&
            !isRemovingItem
          ) {
            isRemovingItem = true;
          }
        }

        const result = await clonedResponse.json();

        for (const responseBodyKeyword of responseBodyKeywords.carts) {
          if (!cart) {
            const newObj = Object.assign({}, result);
            cart = findPropInObject(
              newObj,
              responseBodyKeyword,
              newObj,
              newObj
            );
          }
        }

        if (cart) {
          for (const productName of responseBodyKeywords.productNames) {
            if (!cartItemInfo.name) {
              cartItemInfo.name = Array.isArray(cart)
                ? cart[0][productName]
                : cart[productName];
            }
          }

          for (const productPrice of responseBodyKeywords.productPrices) {
            if (!cartItemInfo.price) {
              cartItemInfo.price = Array.isArray(cart)
                ? cart[0][productPrice]
                : cart[productPrice];
            }
          }

          for (const id of responseBodyKeywords.ids) {
            if (!cartItemInfo.id) {
              cartItemInfo.id = Array.isArray(cart)
                ? cart[0][id]
                : (addedToCardInfo.id = cart[id]);
            }
          }

          payload.value = cartItemInfo;
          payload.cart = cart;
        }

        if (isAddingToCart) {
          that.emit('analytics', {
            type: 'added-to-cart-click',
            value: JSON.stringify(payload),
          });
        }

        if (isRemovingItem) {
          that.emit('analytics', {
            type: 'removed-item-click',
            value: JSON.stringify(payload),
          });
        }
      } catch {
        clickstreamLogger.info('error intercepting the request');
      }

      return res;
    };

    clickstreamLogger.debug(
      `${capitalize(which)} on element: `,
      target,
      `value: ${value} position (${x}, ${y}) relativePosition (${relativeX}, ${relativeY})`
    );

    this.emit('analytics', {
      type: 'mouse-click',
      value,
      processedTarget: matching.length
        ? matching.join(', ')
        : pathToString(target),
      target,
      which,
      x,
      y,
      relativeX,
      relativeY,
    });

    if (isCreateOrderButton) {
      this.emit('analytics', {
        type: 'create-order-button-click',
        value: true.toString(),
      });
    }

    if (isItemDetailsButton) {
      this.emit('analytics', {
        type: 'move-to-item-details-click',
        value: true.toString(),
      });
    }

    if (isOrderSuccessful) {
      this.emit('analytics', {
        type: 'successful-order-click',
        value: true.toString(),
      });
    }

    if (isOrderUnsuccessful) {
      this.emit('analytics', {
        type: 'unsuccessful-order-click',
        value: true.toString(),
      });
    }
  }

  /**
   * Если value пуст ищем его елементом выше.
   *
   * @param target элемент из которого тащим value.
   * @param step текущий шаг рекурсии.
   * @return {string|*}
   */
  getValueIfEmpty = (target, step = 1) => {
    const value = target.textContent.replace(spaces, ' ').trim();

    if (value && value !== ' ' && value.length <= MAX_VALUE_LENGTH) {
      return value;
    }

    if (value.length > MAX_VALUE_LENGTH) {
      return value.substr(0, MAX_VALUE_LENGTH);
    }

    if (step > MAX_CURRENT_STEP) {
      return value;
    }

    const currentStep = step + 1;

    return this.getValueIfEmpty(target.parentNode, currentStep);
  }

  /**
   * Инициализация массива с историей движений курсора мыши первоначальным значением.
   * @param e
   * return void
   */
  addInitialValueToMouseHistory = (e) => {
    const currentMousePosition = [e.pageX, e.pageY];
    this.mouseHistory.push(currentMousePosition);
    this.makeCircleMovementLog(e);
  }

  /**
   * Добавление значения координаты дуги в массив.
   * @param e
   */
  addValueToMouseHistory = (e) => {
    const pointsCount = MouseListener.POINTS_OF_CURVATURE_COUNT;

    // Предыдущая позиция точки дуги.
    const length = this.mouseHistory.length;
    const prev = this.mouseHistory[length - 1];
    const prevX = prev[0];
    const prevY = prev[1];

    const x = e.pageX;
    const y = e.pageY;

    const lineLength = getLineLength({
      begin: { x: prevX, y: prevY },
      end: { x, y },
    });

    // Вычисляем среднюю длину отрезка.
    if (!this.middleLineLength) {
      return this.calculateMiddleLineLength(lineLength);
    }

    // Если массив не переполнен и длинна отрезка примерно равна предыдущему,
    // добавляем точку в массив значений.

    if (
      this.mouseHistory.length < pointsCount &&
      this.middleLineLength &&
      lineLength > this.middleLineLength * 0.4 &&
      lineLength < this.middleLineLength * 1.6
    ) {
      const currentMousePosition = [x, y];
      this.mouseHistory.push(currentMousePosition);
      this.makeCircleMovementLog(e);
    }
  }
  /**
   * Очищение вспомогательных переменные логов кругового движения.
   */
  clearCircleMovementData = () => {
    this.mouseHistoryString = '';
    this.mouseHistory.length = 0;
    this.mouseHistory = [];
    this.circleMovementLogs.length = 0;
    this.circleMovementLogs = [];
  }

  /**
   *  Отправление логов о наличии кругового движения на бэк.
   */
  sendCircleMovementData = () => {
    const movementLogs = MouseListener.formatMovementLogs(
      this.circleMovementLogs
    );

    this.emit('analytics', {
      type: 'mouse-circle-movement',
      ...movementLogs,
    });
  }

  /**
   * Фиксируем логи кругового движения мыши.
   * @param e Событие курсора мыши.
   *
   * Объект с логами.
   */
  makeCircleMovementLog = (e) => {
    const [target, x, y] = [e.target, e.pageX, e.pageY];

    const result = {};
    const matching = findMatchingRulesForTree(
      target,
      this.nodeMatchingRules,
      result
    );
    const { x: relativeX, y: relativeY } = relativize(x, y, result.node);

    const processedTarget = matching.length
      ? matching.join(', ')
      : pathToString(target);

    const value = this.getValue(target);
    const movementLog = {
      value,
      target,
      processedTarget,
      x,
      y,
      relativeX,
      relativeY,
    };
    this.circleMovementLogs.push(movementLog);
  }

  /**
   * Вычисляем среднюю длинну отрезка, поскольку скорость движения на разных
   * мониторах будет отличаться.
   *
   * @param lineLength Длинна отрезка.
   * @return {number}
   */
  calculateMiddleLineLength(lineLength) {
    if (this.middleLineLengths.length < 100) {
      return this.middleLineLengths.push(lineLength);
    } else {
      this.middleLineLength =
        this.middleLineLengths.reduce((sum, val) => val + sum) / 100;
      this.middleLineLengths = [];
    }
  }

  /**
   * get value from target
   *
   * @param target
   * @returns {*|string}
   */
  getValue = (target) => {
    let value;
    switch (target.tagName) {
      case 'INPUT':
        value =
          target.type === 'checkbox'
            ? target.checked
              ? 'on'
              : 'off'
            : target.value;
        break;
      case 'TEXTAREA':
      case 'SELECT':
        value = target.value;
        break;
      default:
        value = this.getValueIfEmpty(target);
    }

    return value;
  }

  start() {
    document.addEventListener('click', this.onClick, true);
    document.addEventListener('dblclick', this.onClick, true);
    document.addEventListener('contextmenu', this.onClick, true);
    document.addEventListener('pointerdown', this.onClick, true);
    document.addEventListener('mousemove', this.handleCloseMove);
    document.addEventListener('mousemove', this.handleCloseMoveAfter);
    document.addEventListener('mousemove', this.handleCircleMovement);
    overlayDisabledButtons();
  }

  stop() {
    document.removeEventListener('click', this.onClick, true);
    document.removeEventListener('dblclick', this.onClick, true);
    document.removeEventListener('contextmenu', this.onClick, true);
    document.removeEventListener('pointerdown', this.onClick, true);
    document.removeEventListener('mousemove', this.handleCloseMove);
    document.removeEventListener('mousemove', this.handleCloseMoveAfter);
    document.removeEventListener('mousemove', this.handleCircleMovement);
    removeButtonOverlays();
  }
}
